import React, { useEffect, useState } from 'react';
import { Pencil, X, Check, AlertTriangle, AlertCircle } from 'lucide-react';
import { useEditMode } from './EditMode';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';

/** Floating bottom-right control. Visible only to logged-in admins.
 *  Click → toggles edit mode globally. While on, every <Tx k="..." /> on the page
 *  becomes click-to-edit with auto-save on blur. */
export const EditModeFab = () => {
  const { user } = useAuth();
  const { editMode, setEditMode } = useEditMode();
  const { lang, setLang } = useI18n();
  const [authError, setAuthError] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Listen for failed saves so the admin SEES the failure instead of silently
  // having the text revert.
  useEffect(() => {
    const onAuth = () => setAuthError(true);
    const onSaveError = (e) => setSaveError(e?.detail?.message || 'Save failed — please try again');
    window.addEventListener('inline-edit-auth-expired', onAuth);
    window.addEventListener('inline-edit-save-error', onSaveError);
    return () => {
      window.removeEventListener('inline-edit-auth-expired', onAuth);
      window.removeEventListener('inline-edit-save-error', onSaveError);
    };
  }, []);

  if (!user || user.role !== 'admin') return null;

  return (
    <>
      {/* Persistent error banner when an inline save failed */}
      {(authError || saveError) && (
        <div data-testid="edit-mode-error-banner"
             className="fixed top-0 inset-x-0 z-[70] bg-rose-600 text-white text-sm font-semibold py-2 px-4 flex items-center justify-center gap-3 shadow-md">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {authError ? (
            <span>
              Your admin session expired — please{' '}
              <a href="/login" className="underline font-bold">log in again</a>{' '}
              and try editing. <strong>Your changes were NOT saved.</strong>
            </span>
          ) : (
            <span>{saveError}</span>
          )}
          <button
            data-testid="edit-mode-error-dismiss"
            onClick={() => { setAuthError(false); setSaveError(''); }}
            className="ml-2 text-white/80 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {editMode && !authError && !saveError && (
        <div data-testid="edit-mode-banner"
             className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-amber-950 text-sm font-semibold py-2 px-4 flex items-center justify-center gap-3 shadow-md">
          <Pencil className="w-4 h-4" />
          <span>You're editing the public site — click any highlighted text to change it.</span>
          <div className="ml-3 inline-flex items-center gap-1 bg-white/40 rounded-full px-1 py-0.5">
            <button
              data-testid="edit-lang-en"
              onClick={() => setLang('en')}
              className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${lang === 'en' ? 'bg-white text-amber-900' : 'text-amber-900/70 hover:text-amber-900'}`}
            >EN</button>
            <button
              data-testid="edit-lang-no"
              onClick={() => setLang('no')}
              className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${lang === 'no' ? 'bg-white text-amber-900' : 'text-amber-900/70 hover:text-amber-900'}`}
            >NO</button>
          </div>
          <span className="hidden sm:inline text-xs opacity-80">Press <kbd className="bg-white/60 rounded px-1.5">Enter</kbd> to save · <kbd className="bg-white/60 rounded px-1.5">Esc</kbd> to cancel</span>
        </div>
      )}
      <button
        data-testid="edit-mode-fab"
        type="button"
        onClick={() => setEditMode(!editMode)}
        title={editMode ? 'Exit edit mode' : 'Edit text on this page'}
        className={`fixed bottom-6 left-6 z-[60] inline-flex items-center justify-center gap-2 rounded-full font-bold transition-all shadow-2xl ${
          editMode
            ? 'bg-emerald-600 text-white px-5 py-3.5 hover:bg-emerald-700'
            : 'bg-slate-900 text-amber-400 w-14 h-14 hover:bg-slate-800 hover:scale-110'
        }`}
      >
        {editMode ? (
          <>
            <Check className="w-5 h-5" />
            <span>Done editing</span>
          </>
        ) : (
          <Pencil className="w-5 h-5" />
        )}
      </button>
    </>
  );
};

export default EditModeFab;
